import React, { useState } from "react";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import Link from "@material-ui/core/Link";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import IAuthentication from "../../core/contracts/IAuthentication";
import {
  CircularProgress,
  InputLabel,
  Input,
  FormControl,
  InputAdornment,
  IconButton,
  Card,
} from "@material-ui/core";
import WMBack from "../../assets/images/wm_back.png";
import WMLogoOneLiner from "../../assets/icons/WMLogoOneLiner";
import clsx from "clsx";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";

export interface ILoginPageProps {
  onSubmit: (this: void, form: IAuthentication) => void;
  loading?: boolean;
}

interface State {
  amount: string;
  password: string;
  weight: string;
  weightRange: string;
  showPassword: boolean;
}

const useStyles = makeStyles((theme) => {
  const buttonAndLoadingSpacing = theme.spacing(3, 0, 2);

  return {
    main: {
      padding: theme.spacing(3),
    },
    hiddenOverflow: {
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    },
    divStyle: {
      width: "100%",
      height: "100vh",
      backgroundImage: `url(${WMBack})`,
      backgroundSize: "cover",
    },
    paper: {
      marginTop: theme.spacing(3),
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      paddingTop: "50px",
      [theme.breakpoints.down("sm")]: { backgroundColor: "transparent" },
      [theme.breakpoints.up("sm")]: { backgroundColor: theme.customPaper.background.cardBackgroundLightContrastWhite },
      boxShadow: "none",
    },
    avatar: {
      margin: theme.spacing(1),
      backgroundColor: theme.palette.primary.main,
    },
    wmLogo: {
      [theme.breakpoints.down("sm")]: { width: "218px" },
      [theme.breakpoints.up("sm")]: { width: "418px" },
    },
    form: {
      width: "100%", // Fix IE 11 issue.
      marginTop: theme.spacing(1),
    },
    textField: {
      width: "100%",
      paddingBottom: 10,
      [theme.breakpoints.down("sm")]: { color: theme.palette.common.white },
      [theme.breakpoints.up("sm")]: { color: theme.palette.common.black },
    },
    textPrimary: {
      fontSize: 24,
      fontWeight: "bold",
      fontVariant: "h5",
      [theme.breakpoints.down("sm")]: { color: theme.palette.common.white },
      [theme.breakpoints.up("sm")]: { color: theme.customPaper.background.contrast },
    },
    textSecond: {
      fontSize: 12,
      [theme.breakpoints.down("sm")]: { color: theme.palette.common.white, opacity: 1 },
      [theme.breakpoints.up("sm")]: { color: theme.customPaper.background.contrast, opacity: 0.5 },
    },
    textColorCommon: {
      [theme.breakpoints.down("sm")]: {
        "color": `${theme.customPaper.background.contrast} !important`,
      },
      [theme.breakpoints.up("sm")]: {
        "color": `${theme.customPaper.background.contrast} !important`,
      },
    },
    textLabelCommon: {
      [theme.breakpoints.down("sm")]: {
        "color": theme.palette.primary.main,
        "&.Mui-focused": {
          color: theme.palette.primary.main,
        },
      },
      [theme.breakpoints.up("sm")]: {
        "color": theme.palette.common.black,
        "&.Mui-focused": {
          color: theme.palette.primary.main,
        },
      },
    },
    entrar: {
      margin: buttonAndLoadingSpacing,
      borderRadius: 0,
      color: theme.palette.common.white,
    },
    loading: {
      display: "flex",
      justifyContent: "center",
      padding: buttonAndLoadingSpacing,
    },
    section: {
      "& svg": {
        "& .fil0": {
          fill: theme.palette.common.white,
        },
      },
      "textAlign": "center",
    },
    gridLink: {
      textAlign: "right",
    },
  };
});

const LoginPage = (props: ILoginPageProps) => {
  const [formValues, setFormValues] = useState<IAuthentication>({ username: "", password: "" });

  const [values, setValues] = useState<State>({
    amount: "",
    password: "",
    weight: "",
    weightRange: "",
    showPassword: false,
  });

  const { onSubmit, loading } = props;

  const handleInputChange = (e: { target: { name: string; value: string } }) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value.toLocaleLowerCase() });
  };

  const handlePasswordChange = (e: { target: { name: string; value: string } }) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleClickShowPassword = () => {
    setValues({ ...values, showPassword: !values.showPassword });
  };

  const doLogin = () => {
    onSubmit(formValues);
  };

  const classes = useStyles();
  return (
    <section className={clsx(classes.hiddenOverflow, classes.divStyle)}>
      <section className={classes.section}>
        <WMLogoOneLiner className={classes.wmLogo} />
        <Container component="main" maxWidth="xs">
          <CssBaseline />
          <Card className={clsx(classes.paper, classes.main)}>
            <Typography component="h1" className={classes.textPrimary}>
              WM GerenciAR
            </Typography>
            <Typography component="h1" className={classes.textSecond}>
              Seja bem vindo!
            </Typography>
            <form className={classes.form} noValidate>
              <FormControl className={classes.textField}>
                <InputLabel
                  htmlFor="standard-adornment-password"
                  className={classes.textLabelCommon}
                >
                  E-mail cadastrado
                </InputLabel>
                <Input
                  autoComplete="off"
                  required
                  fullWidth
                  id="username"
                  name="username"
                  value={formValues["username"]}
                  onChange={handleInputChange}
                  autoFocus
                  className={classes.textColorCommon}
                />
              </FormControl>
              <FormControl className={classes.textField}>
                <InputLabel
                  htmlFor="standard-adornment-password"
                  className={classes.textLabelCommon}
                >
                  Senha
                </InputLabel>
                <Input
                  autoComplete="off"
                  required
                  fullWidth
                  onChange={handlePasswordChange}
                  name="password"
                  id="password"
                  className={classes.textColorCommon}
                  type={values.showPassword ? "text" : "password"}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                      >
                        {values.showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </FormControl>

              <Grid container>
                <Grid item xs className={classes.gridLink}>
                  <Link href="/forgot_password/">Esqueceu sua senha?</Link>
                </Grid>
              </Grid>

              {!loading ? (
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  className={classes.entrar}
                  onClick={doLogin}
                >
                  Entrar
                </Button>
              ) : (
                <div className={classes.loading}>
                  <CircularProgress />
                </div>
              )}
            </form>
          </Card>
        </Container>
      </section>
    </section>
  );
};

export default LoginPage;
